import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { catalogApi, companyApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  FileUploader,
  Icon,
  Input,
  LoadingState,
  PageHeader,
  PhoneInput,
  ProgressBar,
  Select,
  Textarea,
  useToast,
} from '@/components/ui';
import { applyFieldErrors, errorMessage } from '@/hooks/useApiError';
import { companyProfileSchema } from '@/validations';

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

const VERIFICATION_TONES = {
  VERIFIED: 'success',
  PENDING: 'warning',
  REJECTED: 'danger',
  UNVERIFIED: 'neutral',
};

export function CompanyProfilePage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const {
    data: company,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['company-profile'],
    queryFn: companyApi.getProfile,
  });

  const { data: completion } = useQuery({
    queryKey: ['company-completion'],
    queryFn: companyApi.getCompletion,
  });

  const { data: industries } = useQuery({
    queryKey: ['industries'],
    queryFn: () => catalogApi.listIndustries(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(companyProfileSchema) });

  // Populate the form once the profile arrives.
  useEffect(() => {
    if (!company) return;
    reset({
      name: company.name ?? '',
      industry_id: company.industry?.id ?? '',
      company_size: company.company_size ?? '',
      description: company.description ?? '',
      founded_year: company.founded_year ?? '',
      location: company.location ?? '',
      contact_email: company.contact_email ?? '',
      contact_phone: company.contact_phone ?? '',
      website: company.website ?? '',
      linkedin_url: company.linkedin_url ?? '',
    });
  }, [company, reset]);

  const save = useMutation({
    mutationFn: (values) => companyApi.updateProfile(values),
    onSuccess: (updated) => {
      toast.success('Company profile updated');
      queryClient.setQueryData(['company-profile'], updated);
      void queryClient.invalidateQueries({ queryKey: ['company-completion'] });
      void queryClient.invalidateQueries({ queryKey: ['company-verification'] });
    },
  });

  const uploadLogo = useMutation({
    mutationFn: (file) => companyApi.uploadLogo(file),
    onSuccess: (updated) => {
      toast.success('Logo updated');
      queryClient.setQueryData(['company-profile'], updated);
      void queryClient.invalidateQueries({ queryKey: ['company-completion'] });
    },
    onError: (uploadError) => toast.error(errorMessage(uploadError, 'Could not upload the logo')),
  });

  if (isPending) return <LoadingState label="Loading your company…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  async function onSubmit(values) {
    // Empty strings mean "clear this field" -- send null, not "".
    const payload = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [key, value === '' ? null : value]),
    );
    try {
      await save.mutateAsync(payload);
    } catch (mutationError) {
      if (!applyFieldErrors(mutationError, setError)) {
        toast.error(errorMessage(mutationError, 'Could not save the profile'));
      }
    }
  }

  return (
    <div>
      <PageHeader
        title="Company profile"
        description="This is what students see when they find your internships."
        action={
          <Badge dot tone={VERIFICATION_TONES[company.verification_status] ?? 'neutral'}>
            {company.verification_status.replace('_', ' ')}
          </Badge>
        }
      />

      <div className="grid items-start gap-6 lg:grid-cols-[1fr_340px]">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
          <Card>
            <CardHeader title="Basics" description="Your company name, industry and what you do." />
            <div className="space-y-4">
              <Input
                label="Company name"
                required
                error={errors.name?.message}
                {...register('name')}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <Select
                  label="Industry"
                  error={errors.industry_id?.message}
                  {...register('industry_id')}
                >
                  <option value="">Select an industry</option>
                  {(industries ?? []).map((industry) => (
                    <option key={industry.id} value={industry.id}>
                      {industry.name}
                    </option>
                  ))}
                </Select>
                <Select
                  label="Company size"
                  error={errors.company_size?.message}
                  {...register('company_size')}
                >
                  <option value="">Select a size</option>
                  {COMPANY_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size} employees
                    </option>
                  ))}
                </Select>
              </div>
              <Textarea
                label="About"
                rows={6}
                hint="At least 80 characters to complete this section."
                placeholder="What your company does, and what interns work on."
                error={errors.description?.message}
                {...register('description')}
              />
            </div>
          </Card>

          <Card>
            <CardHeader title="Details" description="Where you are based and when you started." />
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Location"
                placeholder="Amsterdam, Netherlands"
                error={errors.location?.message}
                {...register('location')}
              />
              <Input
                label="Founded"
                type="number"
                placeholder="2015"
                error={errors.founded_year?.message}
                {...register('founded_year')}
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Contact"
              description="Used for candidate questions, never shown publicly."
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Contact email"
                type="email"
                error={errors.contact_email?.message}
                {...register('contact_email')}
              />
              <Controller
                name="contact_phone"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    label="Contact phone"
                    error={errors.contact_phone?.message}
                    {...field}
                  />
                )}
              />
            </div>
          </Card>

          <Card>
            <CardHeader title="Links" description="Where students can read more about you." />
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Website"
                placeholder="https://example.com"
                error={errors.website?.message}
                {...register('website')}
              />
              <Input
                label="LinkedIn"
                placeholder="https://linkedin.com/company/example"
                error={errors.linkedin_url?.message}
                {...register('linkedin_url')}
              />
            </div>
          </Card>

          <div className="flex justify-end gap-2">
            <Button type="submit" isLoading={isSubmitting || save.isPending}>
              Save changes
            </Button>
          </div>
        </form>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Logo" description="Shown on your profile and every listing." />
            {company.logo_url ? (
              <img
                src={resolveFileUrl(company.logo_url)}
                alt={`${company.name} logo`}
                className="border-line mb-4 h-20 w-20 rounded-lg border object-contain"
              />
            ) : (
              <div className="mb-4">
                <EmptyState
                  compact
                  icon={<Icon name="company" size={16} />}
                  title="No logo uploaded yet."
                />
              </div>
            )}
            <FileUploader
              accept="image/jpeg,image/png,image/webp"
              maxSizeBytes={MAX_LOGO_BYTES}
              isUploading={uploadLogo.isPending}
              hint="JPEG, PNG or WebP, up to 2 MB."
              onSelect={(file) => uploadLogo.mutate(file)}
            />
          </Card>

          <Card>
            <CardHeader title="Profile completion" />
            <ProgressBar
              value={completion?.percentage ?? company.profile_completion}
              label="Complete"
            />
            <ul className="mt-4 space-y-2">
              {(completion?.sections ?? []).map((section) => (
                <li key={section.key} className="flex items-start gap-2.5 text-sm">
                  {section.complete ? (
                    <span
                      aria-hidden="true"
                      className="bg-success-50 text-success-700 mt-px grid size-5 shrink-0 place-items-center rounded-full"
                    >
                      <Icon name="check" size={12} />
                    </span>
                  ) : (
                    <span
                      aria-hidden="true"
                      className="border-line mt-px size-5 shrink-0 rounded-full border-2"
                    />
                  )}
                  <span className={section.complete ? 'text-ink-muted' : 'text-ink'}>
                    {section.label}
                    {!section.complete && (
                      <span className="text-ink-muted block text-xs">{section.hint}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
